// Lib
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import * as Immutable from 'immutable';
import { debounce } from 'lodash';

// Singletons
import zoomComponentSingleton from '../../../../canvas/zoom/zoomComponentSingleton';

// Components
import PopupToolButton from '../../../../components/popupPanel/PopupToolButton';
import PopupPanel from '../../../../components/popupPanel/PopupPanel';
import ZoomPopupPanelSection from './ZoomPopupPanelSection';
import PresentationModePopupPanelSection from './PresentationModePopupPanelSection';

// Selectors
import { getCanvasSize } from '../../../../canvas/store/canvasSizeSelector';
import { canvasZoomScaleSelector } from '../../../../canvas/store/canvasZoomSelector';
import { getCurrentUserEducation } from '../../../../user/currentUserSelector';

// Actions
import {
    getCanvasZoomContentRectThunk,
    getZoomToFitContentRectThunk,
} from '../../../../canvas/store/canvasBoundingRectActions';
import { setCanvasZoomTargetScale } from '../../../../canvas/store/canvasActions';

// Utils
import rafThrottle from '../../../../../common/utils/lib/rafThrottle';
import { getZoomTextInUnits } from '../../../../canvas/zoom/zoomDisplayUtils';

// Constants
import { PopupIds, PopupSections } from '../../../../components/popupPanel/popupConstants';
import { USER_NAVIGATE } from '../../../../../common/users/userConstants';

// Types
import { ActionObject } from '../../../../../common/actions/actionTypes';
import { Rect } from '../../../../../common/maths/geometry/rect/rectTypes';

// Styles
import './ViewUtil.scss';

const SHOW_ZOOM_SCALE_CLASS_NAME = 'show-zoom-scale';

const keepViewPopupOpenPredicate = (state: Immutable.Map<string, unknown>, action: ActionObject) =>
    action.type === USER_NAVIGATE || action.type === '@@router/LOCATION_CHANGE';

const mapStateToProps = createStructuredSelector({
    canvasSize: getCanvasSize,
    zoomScale: canvasZoomScaleSelector,
    currentUserEducation: getCurrentUserEducation,
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchGetCanvasZoomContentRectThunk: (canvasViewportClientRect: Rect) =>
        dispatch(getCanvasZoomContentRectThunk(canvasViewportClientRect)),
    dispatchGetZoomToFitContentRectThunk: (canvasViewportNode: HTMLElement) =>
        dispatch(getZoomToFitContentRectThunk(canvasViewportNode)),
    dispatchSetCanvasZoomTargetScale: (scale: number) => dispatch(setCanvasZoomTargetScale({ scale })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type Props = ConnectedProps<typeof connector> & {
    isCurrentBoardFetched: boolean;
    isPresentationModeEnabled: boolean;
};

class ViewUtil extends React.Component<Props> {
    private readonly toolTextContainer: React.RefObject<HTMLDivElement>;
    private readonly toolTextZoomScale: React.RefObject<HTMLSpanElement>;

    constructor(props: Props) {
        super(props);

        zoomComponentSingleton.setViewUtilInstance(this);

        this.toolTextContainer = React.createRef();
        this.toolTextZoomScale = React.createRef();
    }

    componentDidMount() {
        this.manuallySetZoomValue(this.props.zoomScale * 100);
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.zoomScale !== prevProps.zoomScale) {
            this.manuallySetZoomValue(this.props.zoomScale * 100);
        }
    }

    resetPopupTriggerText = debounce((value: number, viewPopupTrigger: HTMLDivElement) => {
        if (value === 100) {
            viewPopupTrigger.classList.remove(SHOW_ZOOM_SCALE_CLASS_NAME);
        }
    }, 1000);

    /**
     * This static function is called by ZoomCanvas to force update the value of the zoom value in view popup trigger
     * button. It is required to make sure this value is updated immediately in a performant way (without having to
     * go through redux state management)
     */
    manuallySetZoomValue = rafThrottle((value: number): void => {
        if (!this.toolTextContainer.current || !this.toolTextZoomScale.current) return;

        if (!this.toolTextContainer.current.classList.contains(SHOW_ZOOM_SCALE_CLASS_NAME)) {
            this.toolTextContainer.current.classList.add(SHOW_ZOOM_SCALE_CLASS_NAME);
        }

        this.toolTextZoomScale.current.innerHTML = getZoomTextInUnits(value);
        this.resetPopupTriggerText(value, this.toolTextContainer.current);
    });

    render() {
        const {
            isCurrentBoardFetched,
            canvasSize,
            zoomScale,
            currentUserEducation,

            dispatchSetCanvasZoomTargetScale,
            dispatchGetCanvasZoomContentRectThunk,
            dispatchGetZoomToFitContentRectThunk,
        } = this.props;

        return (
            <div className="ViewUtil CurrentBoardHeaderUtil">
                <PopupToolButton
                    popupId={PopupIds.VIEW}
                    closeSection={PopupSections.HEADER}
                    stayActivePredicate={keepViewPopupOpenPredicate}
                    text={
                        <div ref={this.toolTextContainer}>
                            <span className="tool-text-view">View</span>
                            <span className="tool-text-zoom-scale" ref={this.toolTextZoomScale}>
                                {getZoomTextInUnits(zoomScale * 100)}
                            </span>
                        </div>
                    }
                    showArrow
                />
                <PopupPanel popupId={PopupIds.VIEW} className="ViewPopup">
                    <ZoomPopupPanelSection
                        isCurrentBoardFetched={isCurrentBoardFetched}
                        canvasSize={canvasSize}
                        zoomScale={zoomScale}
                        currentUserEducation={currentUserEducation}
                        dispatchSetCanvasZoomTargetScale={dispatchSetCanvasZoomTargetScale}
                        dispatchGetCanvasZoomContentRectThunk={dispatchGetCanvasZoomContentRectThunk}
                        dispatchGetZoomToFitContentRectThunk={dispatchGetZoomToFitContentRectThunk}
                    />
                    <PresentationModePopupPanelSection />
                </PopupPanel>
            </div>
        );
    }
}

export class ViewUtilType extends ViewUtil {}

export default connector(ViewUtil);
